import type { ComponentType } from 'react';
import { Box } from '@zillow/constellation-10';
import { ErrorPage } from '@zg-rentals/visual-cohesion';
import { rentalManagerLoginOptions, rentalManagerStepUpLoginOptions } from '@zg-rentals/login-zillow';
import { zillowLoginRedirectHref } from '@zg-rentals/login-zillow/client';
import type { RedirectHref } from '@zg-rentals/auth-react/legacy';
import type { useAuth } from '../AuthProvider';
import { Auth, hasAuth } from '../AuthProvider';
import { bootstrap } from '../bootstrap';
import pathsManifest from '../utils/pathsManifest';
import StringConstants from '../constants/StringConstants';

type AuthUser = Exclude<ReturnType<typeof useAuth>['user'], null | undefined>;

const APP_NAME = 'satellite-landlord-web';

const paths = pathsManifest();
const isDevelopment = process.env.NODE_ENV === 'development';
const ctaText = isDevelopment ? 'Return to SLW Dev Tools Page' : StringConstants.GO_TO_RENTAL_MANAGER;
const ctaUrl = isDevelopment ? paths.devToolsPage : paths.homePage;

const onError = (errorMessage: string, errorCode: number) => (
  // Renders in place of the page Component (no Layout/header/footer), so it fills the full
  // viewport rather than using ErrorPageContainer's chrome-aware heights.
  <Box css={{ height: '100dvh' }}>
    <ErrorPage
      errorMessage={errorMessage}
      errorDetails={`Error code: ${errorCode}`}
      action={<a href={ctaUrl}>{ctaText}</a>}
    />
  </Box>
);

const loginRedirectHref = zillowLoginRedirectHref({
  baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL,
  entryPoint: APP_NAME,
  forwardPrefix: process.env.NEXT_PUBLIC_AUTH_FORWARD_PREFIX,
  ...rentalManagerLoginOptions,
});

const mfaStepUpRedirectHref = zillowLoginRedirectHref({
  baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL,
  entryPoint: APP_NAME,
  forwardPrefix: process.env.NEXT_PUBLIC_AUTH_FORWARD_PREFIX,
  ...rentalManagerLoginOptions,
  ...rentalManagerStepUpLoginOptions,
});

const mfaRedirectHref: RedirectHref = (href, params) => {
  bootstrap.monitor.count({ name: 'mfa step-up attempt total' }, { name: 'mfa step-up attempt type MfaStepUpAttempt' });

  return mfaStepUpRedirectHref(href, params);
};

const onMfaRedirectAuthorized = () =>
  bootstrap.monitor.count({ name: 'mfa step-up success total' }, { name: 'mfa step-up success type MfaStepUpSuccess' });

const defaultBoundaryErrorProps = {
  onError: onError('unknown error', 500),
  onUnauthorized: () => onError('unauthorized', 401),
};

export function withRedirectBoundary<Props extends { user: AuthUser }>(
  Component: ComponentType<Props>,
  boundaryProps?: { loading?: React.ReactNode },
) {
  const Wrapped = (props: Omit<Props, 'user'>) => {
    const redirecting = boundaryProps?.loading ?? <>Redirecting...</>;

    return (
      <Auth.Boundary
        {...defaultBoundaryErrorProps}
        {...boundaryProps}
        redirectHref={loginRedirectHref}
        redirecting={redirecting}
      >
        <Auth.Boundary
          {...defaultBoundaryErrorProps}
          {...boundaryProps}
          isAuthorized={hasAuth}
          redirectHref={mfaRedirectHref}
          redirecting={redirecting}
          onRedirectAuthorized={onMfaRedirectAuthorized}
          searchParam={['sp', 'x']}
        >
          {(authorizedUser) => <Component {...(props as Props)} user={authorizedUser} />}
        </Auth.Boundary>
      </Auth.Boundary>
    );
  };
  Wrapped.displayName = `withRedirectBoundary(${Component.displayName ?? Component.name ?? 'Component'})`;
  return Wrapped;
}
